import { DateTime } from 'luxon'

/** Fixed business timezone (UTC-4, no DST). */
export const BUSINESS_TIME_ZONE = 'America/La_Paz'

const CALENDAR_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

/** Current instant in the business timezone. */
export function businessNow(): DateTime {
  return DateTime.now().setZone(BUSINESS_TIME_ZONE)
}

/** Business-local calendar date `YYYY-MM-DD` for today. */
export function businessToday(): string {
  return businessNow().toISODate() ?? DateTime.utc().toISODate() ?? ''
}

/** Current instant as a canonical UTC ISO string (`...Z`). */
export function nowUtcIso(): string {
  return DateTime.utc().toISO() ?? new Date().toISOString()
}

/** True for a strict `YYYY-MM-DD` calendar date string. */
export function isCalendarDate(value: unknown): value is string {
  return typeof value === 'string' && CALENDAR_DATE_REGEX.test(value)
}

/**
 * Normalizes a date-only API value to `YYYY-MM-DD`. Accepts a plain calendar
 * date or a legacy `YYYY-MM-DDT...` timestamp (the leading date is kept, since
 * a `DATE` column has no timezone). Returns `null` when nothing usable remains.
 */
export function normalizeCalendarDate(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null
  }

  const prefix = value.slice(0, 10)
  return isCalendarDate(prefix) ? prefix : null
}

/** Validates a date-only string, returning it or `null`. */
export function parseCalendarDate(value: string | null | undefined): string | null {
  return normalizeCalendarDate(value)
}

/**
 * Displays a calendar date as `dd/MM/yyyy` without any timezone shift.
 * Returns an empty string when the value is missing or invalid.
 */
export function formatCalendarDate(
  value: string | null | undefined,
  locale = 'es-BO',
): string {
  const normalized = normalizeCalendarDate(value)

  if (!normalized) {
    return ''
  }

  const parsed = DateTime.fromISO(normalized, { zone: BUSINESS_TIME_ZONE })

  return parsed.isValid
    ? parsed.setLocale(locale).toFormat('dd/MM/yyyy')
    : ''
}

/** Converts an instant to a business-local calendar date `YYYY-MM-DD`. */
export function instantToBusinessDate(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null
  }

  const parsed = DateTime.fromISO(value, { zone: 'utc' }).setZone(
    BUSINESS_TIME_ZONE,
  )

  return parsed.isValid ? (parsed.toISODate() ?? null) : null
}

function formatInstant(
  value: string | null | undefined,
  zone: string,
  options: Intl.DateTimeFormatOptions,
): string {
  if (!value) {
    return ''
  }

  const parsed = DateTime.fromISO(value, { zone: 'utc' }).setZone(zone)

  return parsed.isValid
    ? parsed.setLocale('es-BO').toLocaleString(options)
    : ''
}

/**
 * Displays an instant date in the business timezone (`dd/MM/yyyy`).
 * Returns an empty string when the value is missing or invalid.
 */
export function formatUtcInstantDate(
  value: string | null | undefined,
  zone = BUSINESS_TIME_ZONE,
): string {
  return formatInstant(value, zone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
}

/**
 * Displays an instant time in the business timezone (`HH:mm`).
 * Returns an empty string when the value is missing or invalid.
 */
export function formatUtcInstantTime(
  value: string | null | undefined,
  zone = BUSINESS_TIME_ZONE,
): string {
  return formatInstant(value, zone, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Displays an instant date-time in the business timezone.
 * Returns an empty string when the value is missing or invalid.
 */
export function formatUtcInstantDateTime(
  value: string | null | undefined,
  zone = BUSINESS_TIME_ZONE,
): string {
  return formatInstant(value, zone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** UTC instant for the start of a business-local calendar day. */
export function calendarDateToBusinessStartUtc(date: string): string {
  return (
    DateTime.fromISO(date, { zone: BUSINESS_TIME_ZONE }).startOf('day').toUTC().toISO() ??
    ''
  )
}

/** UTC instant for the exclusive end of a business-local calendar day. */
export function calendarDateToBusinessEndUtc(date: string): string {
  return (
    DateTime.fromISO(date, { zone: BUSINESS_TIME_ZONE })
      .plus({ days: 1 })
      .startOf('day')
      .toUTC()
      .toISO() ?? ''
  )
}

export interface BusinessDayUtcWindow {
  from: string
  to: string
}

/** `[D 00:00, D+1 00:00)` in business time, expressed as UTC instants. */
export function businessDayWindow(date: string): BusinessDayUtcWindow {
  return {
    from: calendarDateToBusinessStartUtc(date),
    to: calendarDateToBusinessEndUtc(date),
  }
}
